import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { useCustomFieldFilterItems } from 'assets/js/pages/engage/workflows/hooks/useCustomFieldFilterItems';
import { AbandonedDonationFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/AbandonedDonationForm';
import { AttendedEventFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/AttendedEvent';
import { BecameRecurringDonorValuesValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/BecameRecurringDonor';
import { CanceledRecurringPlanFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/CanceledRecurringPlan';
import { DidNotWinAuctionFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/DidNotWinAuction';
import { DonationMadeFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/DonationMade';
import { FirstTimeDonationFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/FirstTimeDonation';
import { IssuedTicketFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/IssuedTicket';
import { LastGiftMadeFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/LastGiftMade';
import { RecurringPlanAnniversaryFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/RecurringPlanAnniversary';
import { RegisteredForAuctionFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/RegisteredForAuction';
import { SupporterBirthdayFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/SupporterBirthday';
import { TagFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/Tag';
import { TeamMemberMilestoneFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/TeamMemberMilestone';
import { TeamMemberRegisteredFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/TeamMemberRegisteredTrigger';
import { TotalContributionsFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/TotalContributions';
import { WidgetSubscribedFormValuesDisplay } from 'assets/js/pages/engage/workflows/triggers/WidgetSubscribed';
import { triggerNodeFilter } from 'assets/js/pages/engage/workflows/utils/store';
import { transformStepsToNodes } from 'assets/js/pages/engage/workflows/utils/transformers';
import { NodeWithData } from 'assets/js/pages/engage/workflows/utils/types';
import { WorkflowTriggerTypeEnum } from 'constants/workflows';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { useCurrentWorkflowSteps } from 'customHooks/useCurrentWorkflowSteps';

const EditWorkflowReviewTriggerRow = () => {
    const history = useHistory();
    const { data: customFieldOptions } = useCustomFieldFilterItems();
    const { data: workflow } = useCurrentWorkflow();
    const { data: workflowSteps } = useCurrentWorkflowSteps({
        version: workflow.has_draft_version
            ? workflow.current_version + 1
            : workflow.current_version,
    });

    const nodes = useMemo(
        () =>
            transformStepsToNodes({
                steps: workflowSteps,
                workflow,
                selectable: false,
                customFieldOptions,
            }),
        [workflow, workflowSteps, customFieldOptions],
    );

    const triggerNode = nodes.find(triggerNodeFilter);
    if (!triggerNode) return;

    return (
        <div className="flex items-center justify-between px-6 py-4">
            <TriggerValuesDisplaySwitcher node={triggerNode} />
            <Button
                variant="transparent"
                size="sm"
                onClick={() => history.push('./build', { selectedNodeId: triggerNode.id })}
            >
                Edit
            </Button>
        </div>
    );
};

interface TriggerValuesDisplaySwitcherProps {
    node: NodeWithData;
}
const TriggerValuesDisplaySwitcher = ({ node }: TriggerValuesDisplaySwitcherProps) => {
    if (!node.data?.formData) return null;
    const { type, formData } = node.data;

    switch (type) {
        case WorkflowTriggerTypeEnum.ABANDONED_DONATION_FORM:
            return <AbandonedDonationFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.ATTENDED_EVENT:
            return <AttendedEventFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.PLAN_CREATED:
            return <BecameRecurringDonorValuesValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.PLAN_CANCELED:
            return <CanceledRecurringPlanFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.DID_NOT_WIN_AUCTION:
            return <DidNotWinAuctionFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.DONATION_CREATED:
            return <DonationMadeFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.CONTACT_FIRST_DONATION:
            return <FirstTimeDonationFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.CONTACT_LAST_DONATION:
            return <LastGiftMadeFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.TICKET_CREATED:
            return <IssuedTicketFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.PLAN_ANNIVERSARY:
            return <RecurringPlanAnniversaryFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.CONTACT_BIRTHDAY:
            return <SupporterBirthdayFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.CONTACT_TAGGED:
            return <TagFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.TEAM_MEMBER_MILESTONE:
            return <TeamMemberMilestoneFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.TEAM_MEMBER_REGISTERED:
            return <TeamMemberRegisteredFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.CONTACT_TOTAL_CONTRIBUTIONS:
            return <TotalContributionsFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.WIDGET_SUBSCRIBED:
            return <WidgetSubscribedFormValuesDisplay values={formData.values} showLabel />;
        case WorkflowTriggerTypeEnum.REGISTERED_FOR_AUCTION:
            return <RegisteredForAuctionFormValuesDisplay values={formData.values} showLabel />;
        default:
            return null;
    }
};

export { EditWorkflowReviewTriggerRow };
